import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Stack,
  Chip,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tabs,
  Tab,
  Grid,
  CircularProgress,
  useTheme,
  alpha,
} from "@mui/material";
import {
  WhatsApp as WhatsAppIcon,
  CheckCircleOutline as CheckIcon,
  LockOutlined as LockIcon,
  InfoOutlined as InfoIcon,
  PhoneAndroid as PhoneAndroidIcon,
  PhoneIphone as PhoneIphoneIcon,
  Schedule as ScheduleIcon,
} from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { workspaceScopedQueryKey } from "../../utils/workspaceQueryScope";
import {
  getPersonalWhatsappStatus,
  createPersonalWhatsappSession,
  getPersonalWhatsappQrcode,
  deletePersonalWhatsappSession,
} from "../../api/client";
import { readPrimaryProjectId } from "../../utils/primaryProjectId";
import {
  personalWhatsappErrorToStrings,
  personalWhatsappQrEmptyStrings,
} from "./personalWhatsappErrorUi";
import ApiErrorIntegrationDebug from "../../components/ApiErrorIntegrationDebug";

/** API Fácil: QR válido por ~20s (doc WhatsApp QRCode). */
const PERSONAL_WA_QR_TTL_MS = 20_000;

const WA_HEADER_BG = "#128C7E";
const WA_HEADER_BG_HOVER = "#0f7a6e";
const WA_PANEL_BG_LIGHT = "#ECE5DD";

function usePersonalWaTheme() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return useMemo(
    () => ({
      isDark,
      headerBg: WA_HEADER_BG,
      panelBg: isDark ? alpha(WA_HEADER_BG, 0.14) : WA_PANEL_BG_LIGHT,
      panelBorder: isDark
        ? `1px solid ${alpha(WA_HEADER_BG, 0.35)}`
        : "none",
      iconColor: isDark ? theme.palette.success.light : WA_HEADER_BG,
      qrBoxBg: isDark ? theme.palette.background.paper : theme.palette.common.white,
      qrBoxBorder: isDark ? alpha(theme.palette.divider, 0.8) : undefined,
      tabsBg: isDark
        ? alpha(theme.palette.common.white, 0.06)
        : alpha(theme.palette.common.white, 0.6),
      outlinedBtnBg: theme.palette.background.paper,
      successAlertSx: {
        bgcolor: isDark
          ? alpha(theme.palette.success.main, 0.16)
          : alpha(theme.palette.common.white, 0.95),
        color: theme.palette.text.primary,
        "& .MuiAlert-icon": { color: theme.palette.success.main },
      },
    }),
    [isDark, theme]
  );
}

function qrDataToImgSrc(qr) {
  if (qr == null || qr === "") return null;
  let payload = qr;
  if (typeof qr === "object") {
    const inner = qr.data != null && typeof qr.data === "object" ? qr.data : null;
    payload =
      (typeof qr.data === "string" ? qr.data : null) ??
      inner?.qrcode ??
      inner?.base64 ??
      qr.qrcode ??
      null;
  }
  if (payload == null || payload === "") return null;
  if (typeof payload !== "string") return null;
  const s = payload;
  if (s.startsWith("data:")) return s;
  if (/^https?:\/\//i.test(s)) return s;
  return `data:image/png;base64,${s}`;
}

function normalizeQrcodeFromPayload(data) {
  const raw = data?.qrcode ?? data?.data?.qrcode;
  if (typeof raw === "string") return raw;
  if (raw && typeof raw === "object") {
    return raw?.data?.qrcode ?? raw?.data?.base64 ?? raw?.qrcode ?? null;
  }
  return null;
}

export default function PersonalWhatsappProfileSection() {
  const { t } = useTranslation("profile");
  const waTheme = usePersonalWaTheme();
  const queryClient = useQueryClient();
  const primaryProjectId = readPrimaryProjectId();

  const [personalWaQr, setPersonalWaQr] = useState(null);
  const [personalWaQrExpiresAt, setPersonalWaQrExpiresAt] = useState(null);
  const [qrOsTab, setQrOsTab] = useState(0);
  const [, setQrTick] = useState(0);
  const [waError, setWaError] = useState(null);

  const { data: personalWaStatus } = useQuery(
    ["personal-whatsapp-status"],
    async () => {
      const r = await getPersonalWhatsappStatus();
      return r?.data ?? r ?? {};
    },
    { refetchInterval: 5000 }
  );

  const refreshPersonalWaQr = useMutation(
    async () => {
      const r = await getPersonalWhatsappQrcode();
      return r?.data ?? r ?? {};
    },
    {
      onMutate: () => setWaError(null),
      onSuccess: (data) => {
        const q = normalizeQrcodeFromPayload(data);
        if (q) {
          setPersonalWaQr(q);
          setPersonalWaQrExpiresAt(Date.now() + PERSONAL_WA_QR_TTL_MS);
          setWaError(null);
        } else {
          setWaError(personalWhatsappQrEmptyStrings(t));
        }
      },
      onError: (err) => {
        const errCode =
          err?.response?.data?.code ?? err?.code ?? null;
        if (errCode === "personal_whatsapp.no_session" || errCode === "errors.personal_whatsapp.no_session") {
          setPersonalWaQr(null);
          setPersonalWaQrExpiresAt(null);
          queryClient.invalidateQueries(["personal-whatsapp-status"]);
        }
        setWaError(personalWhatsappErrorToStrings(err, t));
      },
    }
  );

  const deletePersonalWa = useMutation(
    async () => {
      await deletePersonalWhatsappSession();
    },
    {
      onMutate: () => setWaError(null),
      onSuccess: () => {
        setPersonalWaQr(null);
        setPersonalWaQrExpiresAt(null);
        setWaError(null);
        queryClient.invalidateQueries(["personal-whatsapp-status"]);
        queryClient.invalidateQueries(workspaceScopedQueryKey("me"));
        queryClient.invalidateQueries(["personal-whatsapp-chats"]);
      },
      onError: (err) =>
        setWaError(personalWhatsappErrorToStrings(err, t, { mode: "disconnect" })),
    }
  );

  const createPersonalWa = useMutation(
    async () => {
      const r = await createPersonalWhatsappSession({
        projectId: primaryProjectId != null ? Number(primaryProjectId) : undefined,
      });
      return r?.data ?? r ?? {};
    },
    {
      onMutate: () => setWaError(null),
      onSuccess: (data) => {
        const q = normalizeQrcodeFromPayload(data);
        if (q) {
          setPersonalWaQr(q);
          setPersonalWaQrExpiresAt(Date.now() + PERSONAL_WA_QR_TTL_MS);
          setWaError(null);
        }
        queryClient.invalidateQueries(["personal-whatsapp-status"]);
        queryClient.invalidateQueries(workspaceScopedQueryKey("me"));
        queryClient.invalidateQueries(["personal-whatsapp-chats"]);
        if (!q) {
          setTimeout(() => refreshPersonalWaQr.mutate(), 3000);
        }
      },
      onError: (err) => setWaError(personalWhatsappErrorToStrings(err, t)),
    }
  );

  const dismissWaError = useCallback(() => {
    setWaError(null);
    createPersonalWa.reset();
    deletePersonalWa.reset();
    refreshPersonalWaQr.reset();
  }, [createPersonalWa, deletePersonalWa, refreshPersonalWaQr]);

  const connected = personalWaStatus?.status === "connected";
  const enabled = personalWaStatus?.enabled;
  const awaitingConnection = enabled && !connected;

  useEffect(() => {
    if (connected || personalWaStatus?.enabled === false) {
      setPersonalWaQr(null);
      setPersonalWaQrExpiresAt(null);
    }
  }, [connected, personalWaStatus?.enabled]);

  const qrSrc = qrDataToImgSrc(personalWaQr);
  const now = Date.now();
  const qrExpired =
    Boolean(qrSrc) && Boolean(personalWaQrExpiresAt) && now > personalWaQrExpiresAt;

  useEffect(() => {
    if (!qrSrc || connected) return undefined;
    const id = setInterval(() => setQrTick((x) => x + 1), 1000);
    return () => clearInterval(id);
  }, [qrSrc, connected]);

  const stepsAndroid = t("personalWhatsapp.stepsAndroid", { returnObjects: true });
  const stepsIos = t("personalWhatsapp.stepsIos", { returnObjects: true });
  const listAndroid = Array.isArray(stepsAndroid) ? stepsAndroid : [];
  const listIos = Array.isArray(stepsIos) ? stepsIos : [];

  const bullets = [
    { key: "noHistory", text: t("personalWhatsapp.bulletNoHistory"), icon: CheckIcon },
    { key: "reconnect", text: t("personalWhatsapp.bulletReconnect"), icon: InfoIcon },
    { key: "private", text: t("personalWhatsapp.bulletPrivate"), icon: LockIcon },
    { key: "promote", text: t("personalWhatsapp.bulletPromote"), icon: InfoIcon },
    { key: "filter", text: t("personalWhatsapp.bulletFilter"), icon: CheckIcon },
    { key: "expiry", text: t("personalWhatsapp.bulletExpiry"), icon: ScheduleIcon },
  ];

  return (
    <Paper
      sx={{
        borderRadius: 3,
        overflow: "hidden",
        boxShadow: (th) => th.shadows[2],
      }}
    >
      <Box
        sx={{
          bgcolor: WA_HEADER_BG,
          color: "common.white",
          px: 2.5,
          py: 2,
          display: "flex",
          alignItems: "flex-start",
          gap: 1.5,
        }}
      >
        <WhatsAppIcon sx={{ fontSize: 36, color: "common.white", mt: 0.25 }} />
        <Box>
          <Typography variant="h6" sx={{ color: "common.white", fontWeight: 700, lineHeight: 1.2 }}>
            {t("personalWhatsapp.section")}
          </Typography>
          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.9)", mt: 0.5 }}>
            {t("personalWhatsapp.headerSubtitle")}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          bgcolor: waTheme.panelBg,
          color: "text.primary",
          borderTop: waTheme.panelBorder,
          p: { xs: 2, sm: 2.5 },
        }}
      >
        <Typography variant="body2" sx={{ mb: 2, color: "text.primary" }}>
          {t("personalWhatsapp.intro")}
        </Typography>

        <List dense disablePadding sx={{ mb: 2 }}>
          {bullets.map(({ key, text, icon: Icon }) => (
            <ListItem key={key} disableGutters sx={{ py: 0.35, alignItems: "flex-start" }}>
              <ListItemIcon sx={{ minWidth: 36, mt: 0.25 }}>
                <Icon sx={{ fontSize: 20, color: waTheme.iconColor }} />
              </ListItemIcon>
              <ListItemText
                primary={text}
                primaryTypographyProps={{ variant: "body2", color: "text.primary" }}
              />
            </ListItem>
          ))}
        </List>

        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 2 }}>
          <Chip
            size="small"
            color={connected ? "success" : enabled ? "warning" : "default"}
            sx={{ fontWeight: 600 }}
            label={
              connected
                ? t("personalWhatsapp.connected")
                : enabled
                  ? t("personalWhatsapp.awaitingQr")
                  : t("personalWhatsapp.disconnected")
            }
          />
        </Stack>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 2 }}>
          {!enabled ? (
            <Button
              variant="contained"
              startIcon={<WhatsAppIcon />}
              disabled={createPersonalWa.isLoading}
              onClick={() => createPersonalWa.mutate()}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                bgcolor: WA_HEADER_BG,
                color: "common.white",
                "&:hover": { bgcolor: WA_HEADER_BG_HOVER },
              }}
            >
              {createPersonalWa.isLoading ? t("personalWhatsapp.creating") : t("personalWhatsapp.connect")}
            </Button>
          ) : (
            <>
              <Button
                variant="outlined"
                color="error"
                disabled={deletePersonalWa.isLoading}
                onClick={() => deletePersonalWa.mutate()}
                sx={{ textTransform: "none", fontWeight: 700, bgcolor: waTheme.outlinedBtnBg }}
              >
                {t("personalWhatsapp.disconnect")}
              </Button>
              {!connected && (
                <Button
                  variant="outlined"
                  disabled={refreshPersonalWaQr.isLoading}
                  onClick={() => refreshPersonalWaQr.mutate()}
                  sx={{ textTransform: "none", fontWeight: 700, bgcolor: waTheme.outlinedBtnBg }}
                >
                  {t("personalWhatsapp.refreshQr")}
                </Button>
              )}
            </>
          )}
          {connected && (
            <Button
              component={RouterLink}
              to="/chat"
              variant="contained"
              sx={{
                textTransform: "none",
                fontWeight: 700,
                bgcolor: WA_HEADER_BG,
                color: "common.white",
                "&:hover": { bgcolor: WA_HEADER_BG_HOVER },
              }}
            >
              {t("personalWhatsapp.goToChat")}
            </Button>
          )}
        </Stack>

        {waError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={dismissWaError}>
            <Typography component="div" variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
              {waError.title}
            </Typography>
            <Typography component="div" variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
              {waError.body}
            </Typography>
            <ApiErrorIntegrationDebug integrationDebug={waError.integrationDebug} />
          </Alert>
        )}

        {connected && (
          <Alert severity="success" sx={{ mb: 0, ...waTheme.successAlertSx }}>
            {t("personalWhatsapp.connectedHint")}
          </Alert>
        )}

        {!connected && (
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} md={5}>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom color="text.primary">
                {t("personalWhatsapp.stepsTitle")}
              </Typography>
              <Tabs
                value={qrOsTab}
                onChange={(_, v) => setQrOsTab(v)}
                variant="fullWidth"
                sx={{
                  minHeight: 40,
                  bgcolor: waTheme.tabsBg,
                  borderRadius: 1,
                  mb: 1,
                  "& .MuiTab-root": {
                    minHeight: 40,
                    textTransform: "none",
                    fontWeight: 600,
                    color: "text.secondary",
                  },
                  "& .Mui-selected": { color: "text.primary" },
                  "& .MuiTabs-indicator": { bgcolor: waTheme.iconColor },
                }}
              >
                <Tab icon={<PhoneAndroidIcon fontSize="small" />} iconPosition="start" label={t("personalWhatsapp.tabAndroid")} />
                <Tab icon={<PhoneIphoneIcon fontSize="small" />} iconPosition="start" label={t("personalWhatsapp.tabIos")} />
              </Tabs>
              <List dense disablePadding>
                {(qrOsTab === 0 ? listAndroid : listIos).map((line, i) => (
                  <ListItem key={i} disableGutters sx={{ py: 0.25, alignItems: "flex-start" }}>
                    <ListItemIcon sx={{ minWidth: 28, mt: 0.2 }}>
                      <Typography variant="caption" fontWeight={700} color="text.secondary">
                        {i + 1}.
                      </Typography>
                    </ListItemIcon>
                    <ListItemText
                      primary={line}
                      primaryTypographyProps={{ variant: "body2", color: "text.primary" }}
                    />
                  </ListItem>
                ))}
              </List>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                {t("personalWhatsapp.qrTip")}
              </Typography>
            </Grid>

            <Grid item xs={12} md={7}>
              <Box
                sx={{
                  bgcolor: waTheme.qrBoxBg,
                  borderRadius: 2,
                  p: 2,
                  minHeight: 280,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: waTheme.isDark ? 0 : 1,
                  border: waTheme.isDark ? 1 : 0,
                  borderColor: waTheme.isDark ? "divider" : undefined,
                }}
              >
                {awaitingConnection && !qrSrc && (
                  <Stack alignItems="center" spacing={1}>
                    <CircularProgress size={36} sx={{ color: WA_HEADER_BG }} />
                    <Typography variant="body2" color="text.secondary" textAlign="center">
                      {t("personalWhatsapp.sessionActive")}
                    </Typography>
                  </Stack>
                )}

                {!enabled && (
                  <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ px: 1 }}>
                    {t("personalWhatsapp.scanToConnect")}
                  </Typography>
                )}

                {qrSrc && (
                  <Box sx={{ position: "relative", display: "inline-block", maxWidth: "100%" }}>
                    <Box
                      component="img"
                      alt="QR WhatsApp"
                      src={qrSrc}
                      sx={{
                        maxWidth: 280,
                        width: "100%",
                        height: "auto",
                        borderRadius: 1,
                        border: "1px solid",
                        borderColor: "divider",
                        display: "block",
                        filter: qrExpired ? "grayscale(1) opacity(0.45)" : "none",
                        transition: "filter 0.2s ease",
                      }}
                    />
                    {personalWaQrExpiresAt && !qrExpired && (
                      <Box
                        sx={{
                          position: "absolute",
                          bottom: 8,
                          right: 8,
                          bgcolor: "rgba(0,0,0,0.72)",
                          color: "#fff",
                          px: 1,
                          py: 0.35,
                          borderRadius: 1,
                          fontSize: 12,
                          fontWeight: 600,
                        }}
                      >
                        {t("personalWhatsapp.qrExpiresIn", {
                          seconds: Math.max(0, Math.ceil((personalWaQrExpiresAt - Date.now()) / 1000)),
                        })}
                      </Box>
                    )}
                    {qrExpired && (
                      <Box
                        sx={{
                          position: "absolute",
                          inset: 0,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: "rgba(0,0,0,0.55)",
                          borderRadius: 1,
                          px: 2,
                          textAlign: "center",
                        }}
                      >
                        <Typography variant="body2" sx={{ color: "common.white", mb: 1.5, fontWeight: 600 }}>
                          {t("personalWhatsapp.qrExpired")}
                        </Typography>
                        <Button
                          variant="contained"
                          size="small"
                          disabled={refreshPersonalWaQr.isLoading}
                          onClick={() => refreshPersonalWaQr.mutate()}
                          startIcon={
                            refreshPersonalWaQr.isLoading ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : undefined
                          }
                          sx={{ textTransform: "none", fontWeight: 700 }}
                        >
                          {t("personalWhatsapp.generateNewQr")}
                        </Button>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        )}
      </Box>
    </Paper>
  );
}
